import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import '../../styles/Admin.scss';
import { useAuth } from '../../context/AuthContext';
import AppSpinner from '../../components/AppSpinner';

interface RegistrationDetail {
  _id: string;
  user: {
    _id: string;
    displayName: string;
    fullName: string;
    email: string;
    userId: string;
    age?: number;
    gender?: string;
    phoneNumber?: string;
  };
  content: string;
  qualificationFiles: Array<{
    filename: string;
    originalname: string;
    path: string;
  }>;
  status: string;
  reviewMessage?: string;
  createdAt: string;
}

const AdminCounselorRegistrationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [registration, setRegistration] = useState<RegistrationDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [reviewMessage, setReviewMessage] = useState<string>('');

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchDetail = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/admin/counselor-registrations/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRegistration(response.data);
        if (response.data.reviewMessage) {
          setReviewMessage(response.data.reviewMessage);
        }
      } catch (error) {
        console.error('Error fetching detail:', error);
        alert('정보를 불러오지 못했습니다.');
        navigate('/admin/counselor-registrations');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id, isAuthenticated, navigate]);

  const handleStatusUpdate = async (status: 'approved' | 'rejected') => {
    if (status === 'rejected' && !reviewMessage.trim()) {
      alert('거절 사유를 입력해주세요.');
      return;
    }
    if (!window.confirm(`${status === 'approved' ? '승인' : '거절'} 하시겠습니까?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/admin/counselor-registrations/${id}/status`,
        { status, reviewMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('처리되었습니다.');
      navigate('/admin/counselor-registrations');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('처리에 실패했습니다.');
    }
  };

  if (loading) return <AppSpinner fullscreen tip="Loading..." />;
  if (!registration) return null;

  return (
    <div className="admin-page">
      <h1>상담사 등록 신청 상세</h1>

      <div className="detail-table-container">
        <table className="detail-table">
          <tbody>
            <tr>
              <th>이름</th>
              <td>{registration.user.fullName} ({registration.user.displayName})</td>
            </tr>
            <tr>
              <th>아이디</th>
              <td>{registration.user.userId}</td>
            </tr>
            <tr>
              <th>이메일</th>
              <td>{registration.user.email}</td>
            </tr>
            <tr>
              <th>연락처</th>
              <td>{registration.user.phoneNumber || '-'}</td>
            </tr>
            <tr>
              <th>나이/성별</th>
              <td>
                {registration.user.age || '-'} / {registration.user.gender || '-'}
              </td>
            </tr>
            <tr>
              <th>신청일</th>
              <td>{new Date(registration.createdAt).toLocaleString()}</td>
            </tr>
            <tr>
              <th>현재 상태</th>
              <td>
                <span
                  className={`status-badge ${
                    registration.status === 'approved'
                      ? 'normal'
                      : registration.status === 'rejected'
                        ? 'suspended'
                        : 'pending'
                  }`}
                >
                  {registration.status === 'approved'
                    ? '승인'
                    : registration.status === 'rejected'
                      ? '거절'
                      : '대기'}
                </span>
              </td>
            </tr>
            <tr>
              <th>소개 및 경력</th>
              <td>
                  <ReactQuill value={registration.content} readOnly={true} theme={'bubble'} />
              </td>
            </tr>
            <tr>
              <th>첨부 파일<br />(자격증 사본)</th>
              <td>
                {registration.qualificationFiles.length > 0 ? (
                  <ul className="file-list">
                    {registration.qualificationFiles.map((file, index) => (
                      <li key={index} className="file-item">
                        <a
                          href={`/${file.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>{file.originalname}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  '-'
                )}
              </td>
            </tr>

            {registration.status === 'pending' && (
              <tr>
                <th>심사 의견<br />(거절 시 필수)</th>
                <td>
                  <textarea
                    value={reviewMessage}
                    onChange={e => setReviewMessage(e.target.value)}
                    className="form-input"
                    style={{
                      width: '100%',
                      height: '80px',
                      padding: '10px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      resize: 'vertical',
                    }}
                    placeholder="승인 또는 거절 사유를 입력하세요."
                  />
                </td>
              </tr>
            )}

            {registration.status !== 'pending' && (
              <>
                <tr>
                  <th>심사 결과</th>
                  <td>
                    <span
                      className={`status-badge ${
                        registration.status === 'approved' ? 'normal' : 'suspended'
                      }`}
                    >
                      {registration.status === 'approved' ? '승인됨' : '거절됨'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>심사 의견</th>
                  <td>{registration.reviewMessage || '-'}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="bottom-actions">
        <button className="btn-list" onClick={() => navigate('/admin/counselor-registrations')}>
          ≡ 목록
        </button>
        {registration.status === 'pending' && (
          <div className="action-buttons">
            <button
              className="btn-delete"
              onClick={() => handleStatusUpdate('rejected')}
            >
              <div className="refusal"></div>
              거절
            </button>
            <button
              className="btn-check"
              onClick={() => handleStatusUpdate('approved')}
            >
              <div className="approve"></div>
              승인
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCounselorRegistrationDetailPage;
